import { Input } from "./ui/input";
import ResponsiveModal from "./ResponsiveModal";
import { Button } from "./ui/button";
import { Phone } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "./ui/input-otp";
import Lottie from "lottie-react";
import otpAnimation from "../../public/otp.json";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthProvider";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

interface loginModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function loginModal({
  open,
  onClose,
  onSuccess,
}: loginModalProps) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"enter-phone" | "enter-otp">("enter-phone");

  const { requestOtp, verifyOtp } = useAuth();

  const { i18n, t } = useTranslation();

  const handleContinue = async () => {
    if (step === "enter-phone") {
      requestOtp(phoneNumber);
      setStep("enter-otp");
    } else if (step === "enter-otp") {
      const success = await verifyOtp(phoneNumber, otp);
      if (success) {
        onSuccess();
        setStep("enter-phone");
        setOtp("");
        setPhoneNumber("");
      } else {
        console.log("invalid otp");
        setStep("enter-phone");
        setOtp("");
        setPhoneNumber("");
        onClose();
      }
    }
  };

  const renderEnterPhoneContent = () => {
    return (
      <div
        className="flex flex-col h-full p-6 sm:p-8 gap-6"
        dir={i18n.dir() === "rtl" ? "rtl" : "ltr"}
      >
        {/* Header */}
        <div className="text-center space-y-2 shrink-0">
          <h1 className="text-2xl font-bold text-gray-900">
            {t("welcome")} 👋
          </h1>
          <p className="text-sm text-gray-500">
            {t("enter_your_phone_number_to_continue")}
          </p>
        </div>

        {/* Input */}
        <div className="flex flex-col gap-2 shrink-0">
          <label htmlFor="phone" className="text-sm font-medium text-gray-700">
            {t("phone_number")}
          </label>
          <div className="flex border-2 border-gray-300 hover:border-primary focus-within:border-primary items-center rounded-md bg-white px-3 transition-all duration-300 ">
            <Phone className="h-5 w-5 text-gray-400 shrink-0" />
            <Input
              dir={i18n.dir() === "rtl" ? "rtl" : "ltr"}
              id="phone"
              type="tel"
              placeholder={t("phone_number_placeholder")}
              className="border-0 focus-visible:ring-0 ps-3"
              autoComplete="off"
              inputMode="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1"></div>

        <div className="flex flex-col gap-2 shrink-0 pb-2">
          <Button size="lg" onClick={handleContinue}>
            {t("continue")}
          </Button>
          <p className="text-xs text-center text-gray-400">
            {t("we_ll_send_you_a_one_time_code_to_verify_your_number")}
          </p>
        </div>
      </div>
    );
  };

  const renderOtpContent = () => {
    return (
      <div
        className="flex flex-col h-full p-6 sm:p-8 gap-3"
        dir={i18n.dir() === "rtl" ? "rtl" : "ltr"}
      >
        {/* Header */}
        <div className="text-center space-y-2 shrink-0">
          <h1 className="text-2xl font-bold text-gray-900">
            {t("enter_code")}
          </h1>
          <p className="text-sm text-gray-500">
            {t("we_ve_sent_a_6_digit_code_to_your_phone_number")}
          </p>
        </div>

        {/* OTP Input */}
        <div className="flex justify-center shrink-0 py-2" dir="ltr">
          <InputOTP maxLength={6} value={otp} onChange={setOtp}>
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>

        {/* <div className="flex-1 w-full max-w-xs mx-auto min-h-0 flex items-center justify-center">
          <Lottie
            className="w-full h-full max-h-64"
            animationData={otpAnimation}
            loop={true} // loop the animation
            autoplay={true} // auto start
          />
        </div> */}

        {/* Actions */}
        <div className="flex flex-col gap-3 shrink-0 pb-2">
          <Button
            size="lg"
            className="w-full rounded-xl shadow-md hover:shadow-lg transition"
            onClick={handleContinue}
          >
            {t("verify")}
          </Button>

          <p className="text-xs text-center text-gray-400">
            {t("didnt_get_the_code")}{" "}
            <button className="text-primary font-medium hover:underline">
              {t("resend")}
            </button>
          </p>
        </div>
      </div>
    );
  };

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      renderContent={
        step === "enter-phone" ? renderEnterPhoneContent : renderOtpContent
      }
    />
  );
}
